import { useEffect, useRef, useState, type ReactNode } from 'react';
import { syntheticEntryQuery } from '../content-media/content-media';
import { Icon } from '../ui/Primitives';
import { BusinessWorkspaceSelector } from './BusinessWorkspaceSelector';
import './portal-shell.css';

const menus = {
  provider: [
    ['工作台', '', 'home'],
    ['我的产品', '/products', 'layers'],
    ['产品资料与文档', '/documents', 'file'],
    ['提交与审核', '/submissions', 'clipboard'],
    ['企业资料', '/organization', 'building'],
  ],
  governance: [
    ['工作台', '', 'home'],
    ['审核中心', '/queue', 'clipboard'],
    ['产品管理', '/products', 'layers'],
    ['装备管理', '/assets', 'truck'],
    ['用户与机构', '/organizations', 'users'],
    ['系统设置', '/settings', 'settings'],
  ],
} as const;

export function PortalIcon({ name }: { readonly name: string }) {
  const aliases: Readonly<Record<string, string>> = {
    box: 'layers',
    share: 'network',
    menu: 'layers',
  };
  return <Icon name={aliases[name] ?? name} />;
}

function PortalDialog({
  children,
  close,
  label,
  className = '',
}: {
  readonly children: ReactNode;
  readonly close: () => void;
  readonly label: string;
  readonly className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous instanceof HTMLElement) {
        previous.focus();
        requestAnimationFrame(() => {
          if (previous.isConnected) previous.focus();
        });
      }
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={className}
      aria-label={label}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      {children}
    </dialog>
  );
}

/** Shared presentation only. Menu visibility is not authorization. */
export function PortalShell({
  portal,
  title,
  children,
  mode = 'DEMO',
}: {
  readonly portal: 'provider' | 'governance';
  readonly title: string;
  readonly children: ReactNode;
  readonly mode?: 'DEMO' | 'SYN_LIVE';
}) {
  const live = mode === 'SYN_LIVE';
  // Keep the transport selection while navigating; each feature decides whether it has live data.
  const liveNavigation =
    live ||
    (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('synLive') === '1');
  const explicitDemo =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('synLive') === '0';
  const portalHref = (route: string) =>
    `${route}${liveNavigation ? syntheticEntryQuery() : explicitDemo ? '?synLive=0' : ''}`;
  const [drawer, setDrawer] = useState(false);
  const [notice, setNotice] = useState('');
  const base =
    portal === 'provider'
      ? '/demo/technology-solutions/provider-portal'
      : '/demo/governance-review';
  const path = typeof window === 'undefined' ? base : window.location.pathname.replace(/\/$/, '');
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const state = window.history.state as {
        ca04ReturnFocus?: string;
        ca04ReturnScroll?: number;
        ca04SoftEntry?: boolean;
      } | null;
      if (typeof state?.ca04ReturnFocus === 'string') {
        const previousLink = Array.from(
          document.querySelectorAll<HTMLAnchorElement>('a[href]'),
        ).find((link) => link.getAttribute('href') === state.ca04ReturnFocus);
        previousLink?.focus({ preventScroll: true });
        if (typeof state.ca04ReturnScroll === 'number') window.scrollTo(0, state.ca04ReturnScroll);
      } else if (state?.ca04SoftEntry) {
        // A local view may already have restored its own list focus and scroll.
        const focused = document.activeElement;
        if (!(focused instanceof HTMLElement) || !focused.closest('#portal-main')) {
          document.getElementById('portal-main')?.focus({ preventScroll: true });
          window.scrollTo(0, 0);
        }
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [path]);
  const subtitle = portal === 'provider' ? '厂商端' : '管理员端';
  const navigation = (
    <>
      <a className="portal-brand" href={portalHref(base)} aria-label={`AI消防+救援 ${subtitle}`}>
        <img src="/portals/logo.png" alt="公司原始标识" />
        <strong>AI消防+救援</strong>
        <span>{subtitle}</span>
      </a>
      <nav aria-label={`${portal === 'provider' ? '06 厂商端' : '08 管理端'}一级导航`}>
        {menus[portal].map(([label, suffix, icon]) => {
          const active = suffix === '' ? path === base : path.startsWith(base + suffix);
          return (
            <a
              key={suffix}
              href={portalHref(base + suffix)}
              aria-current={active ? 'page' : undefined}
              className={active ? 'portal-nav-active' : ''}
            >
              <PortalIcon name={icon} />
              <span>{label}</span>
            </a>
          );
        })}
      </nav>
      <div className="portal-sidebar-bottom">
        <a href={portalHref(base)}>返回本端首页</a>
        <button
          onClick={() =>
            setNotice(
              live
                ? 'SYN 测试门户：请在正文登录测试账号，保存与审核结果以后端读回为准。退出会话请使用正文中的退出按钮。'
                : '帮助中心：这是本地交互原型。可查看资料、版本差异和演示反馈；正式提交尚未接入。',
            )
          }
        >
          <PortalIcon name="info" />
          帮助中心
        </button>
        {!live && (
          <button onClick={() => setNotice('当前为 DEV 演示身份，没有真实登录会话可退出。')}>
            <PortalIcon name="arrow" />
            退出登录
          </button>
        )}
      </div>
    </>
  );
  return (
    <div
      className={`portal-shell portal-${portal}`}
      onClickCapture={(event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        const link =
          event.target instanceof Element
            ? event.target.closest<HTMLAnchorElement>('a[href]')
            : null;
        const href = link?.getAttribute('href');
        if (
          !link ||
          !href ||
          link.hasAttribute('download') ||
          (link.target && link.target !== '_self')
        )
          return;
        const destination = new URL(href, window.location.href);
        if (
          destination.origin !== window.location.origin ||
          destination.hash ||
          destination.searchParams.get('synLive') !==
            new URL(window.location.href).searchParams.get('synLive') ||
          !(destination.pathname === base || destination.pathname.startsWith(`${base}/`))
        )
          return;
        event.preventDefault();
        setDrawer(false);
        if (destination.href === window.location.href) return;
        const existing: unknown = window.history.state;
        const navigationState = typeof existing === 'object' && existing !== null ? existing : {};
        window.history.replaceState(
          { ...navigationState, ca04ReturnFocus: href, ca04ReturnScroll: window.scrollY },
          '',
          window.location.href,
        );
        // History carries navigation metadata only; each portal owns its React session.
        window.history.pushState({ ca04SoftEntry: true }, '', destination.href);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }}
    >
      <a className="portal-skip" href="#portal-main">
        跳转到主要内容
      </a>
      <aside className="portal-sidebar">{navigation}</aside>
      <div className="portal-workspace">
        <header className="portal-header">
          <button
            className="portal-menu-button"
            aria-label="打开导航"
            aria-expanded={drawer}
            onClick={() => setDrawer(true)}
          >
            <PortalIcon name="layers" />
          </button>
          <div className="portal-heading">
            <h1>{title}</h1>
            <span className="portal-dev">
              {live
                ? 'SYN 测试环境 · 仅本机访问 · 非生产'
                : '本地交互原型 · DEMO_ONLY · 非正式审核'}
            </span>
          </div>
          <div className="portal-header-actions">
            {live ? (
              <span className="portal-mode">测试会话（账号见正文）</span>
            ) : (
              <>
                {portal === 'provider' ? (
                  <span className="portal-company">
                    XX科技有限公司（演示）
                    <PortalIcon name="chevron" />
                  </span>
                ) : (
                  <span className="portal-mode">
                    <PortalIcon name="shield" />
                    治理模式 · 演示
                  </span>
                )}
                <button
                  aria-label="演示通知"
                  className="portal-notifications"
                  onClick={() => setNotice('演示通知：待办来自本地样例集合，不连接实时消息。')}
                >
                  <PortalIcon name="bell" />
                  <i />
                </button>
                <button
                  className="portal-user"
                  aria-label="查看演示身份"
                  onClick={() => setNotice('当前身份为演示样例，企业、管理员和机构身份均未核实。')}
                >
                  <span>
                    <PortalIcon name="user" />
                  </span>
                  <b>{portal === 'provider' ? '张工' : '管理员'}</b>
                  <PortalIcon name="chevron" />
                </button>
              </>
            )}
          </div>
        </header>
        <main id="portal-main" tabIndex={-1}>
          <BusinessWorkspaceSelector />
          {children}
        </main>
      </div>
      {drawer && (
        <PortalDialog
          className="portal-drawer portal-shell"
          label="门户导航抽屉"
          close={() => setDrawer(false)}
        >
          <button
            className="portal-drawer-close"
            aria-label="关闭导航"
            onClick={() => setDrawer(false)}
          >
            <PortalIcon name="close" />
          </button>
          <div className="portal-sidebar">{navigation}</div>
        </PortalDialog>
      )}
      {notice && (
        <PortalDialog
          className="portal-notice portal-shell"
          label={live ? '测试门户提示' : '演示提示'}
          close={() => setNotice('')}
        >
          <h2>{live ? '测试门户提示' : '演示提示'}</h2>
          <p>{notice}</p>
          <button className="portal-button" onClick={() => setNotice('')}>
            知道了
          </button>
        </PortalDialog>
      )}
    </div>
  );
}
